import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { modeUIContext, showSuccessNotification, setIsEditingForm } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce from 'immer';
import { HomeMember, MemberRole, UserAccountMode } from '@moderepo/mode-internal-apis';
import { CircularProgress } from '@material-ui/core';
import { selectProjectById, ritzDataContext } from '@moderepo/mode-data-state-ritz';
import { CountryDialCodeOption, HomeMemberInfo, HomeMemberInfoProps, CountryData, BaseHomeMemberInfoUpdatableInputs, EMAIL_VALIDATION_REGEX,
    capitalizeAll, getHomeMemberRoleName, BaseInfoCompProps } from '@moderepo/mode-app-components';
import { addHomeMember, fetchHomeMembers, userAppDataContext, selectHomeMember, updateHomeMember } from '@moderepo/mode-data-state-user-app';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import * as Utils from '../../utils/utils';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Constants from '../../utils/constants';



interface InputErrors {
    email: string | null;
    phoneNumber: string | null;
}



export interface HomeMemberInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly userId?: number;                                       // will be undefined if this container is used for add new Member
    readonly newMemberSettings?: {                                  // Only available if this container is used for add new member
        readonly onNewMemberCreated: ()=> void;
        readonly onNewMemberCanceled: ()=> void;
    }

    // This container used HomeMemberInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;
}


/**
 * This is the container for the HomeMemberInfoComp. It provides the logic for the HomeMemberInfoComp which is a dumb component that does
 * not do anything other than displaying data. This container will take care of all the logic e.g. edit member, translate, etc...
 * @param props
 */
export const HomeMemberInfoContainer = (props: HomeMemberInfoContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const selectedMember: HomeMember | undefined = props.userId ? selectHomeMember(appDataState, props.userId) : undefined;
    const userAccountMode = project?.userAccountMode;
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        email: null, phoneNumber: null,
    });


    /**
     * This useEffect is used for loading Member data if it is not loaded. Also, we will only load member data if we have userId. If not, this
     * container is probably used for creating new Member
     */
    useEffect(() => {
        if (props.userId && !selectedMember) {
            // NOTE: we don't have an API to fetch just 1 member so we need to fetch all members for a given home
            appDataDispatch(fetchHomeMembers(props.homeId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [selectedMember, props.userId, props.homeId, appDataDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.userId is undefined
     */
    useEffect(() => {
        if (props.userId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.userId, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            email: null, phoneNumber: null,
        });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On member form input change, we will clear the error message for that field.
     */
    const onMemberInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    /**
     * On add member by email. Make an API call to add a new member with the provided info. If successful,
     * call the onMemberSelected automatically.
     */
    const onAddMemberByEmail = useCallback(async (
        email: string | undefined, role: MemberRole | undefined,
    ) => {
        try {
            // reset the errors
            const newInputErrors: InputErrors = ({
                email: null, phoneNumber: null,
            });

            // Validate inputs
            if (!email) {
                newInputErrors.email = 'error.input.email.empty';
            } else if (!EMAIL_VALIDATION_REGEX.test(email)) {
                newInputErrors.email = 'error.input.email.invalid';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.email && email) {
                await appDataDispatch(addHomeMember(props.homeId, { email, role }));
                uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'terms.member' }));

                closeEditing();
                if (props.newMemberSettings) {
                    props.newMemberSettings.onNewMemberCreated();
                }
            }
        } catch (error) {
            /* ignore error */
        }
    }, [appDataDispatch, props.homeId, props.newMemberSettings, uiDispatch, closeEditing]);



    /**
     * On create phone member. Make an API call to add a new member with the provided info. If successful,
     * call the onMemberSelected automatically.
     */
    const onAddMemberByPhone = useCallback(async (phoneNumber: string | undefined, role: MemberRole | undefined) => {
        try {
            // reset the errors
            const newInputErrors: InputErrors = ({
                email: null, phoneNumber: null,
            });

            // Validate inputs
            if (!phoneNumber) {
                newInputErrors.phoneNumber = 'error.input.phone_number.empty';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.phoneNumber && phoneNumber) {
                await appDataDispatch(addHomeMember(props.homeId, { phoneNumber, role }));
                uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'terms.member' }));

                closeEditing();
                if (props.newMemberSettings) {
                    props.newMemberSettings.onNewMemberCreated();
                }
            }
        } catch (error) {
            /* ignore error */
        }
    }, [appDataDispatch, props.homeId, props.newMemberSettings, uiDispatch, closeEditing]);



    const onUpdateMember = useCallback(async (
        memberId: number | undefined, role: MemberRole | undefined,
    ) => {
        if (selectedMember?.role !== role && memberId && role) {
            try {
                await appDataDispatch(updateHomeMember(props.homeId, memberId, role));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'terms.member' }));

                closeEditing();
            } catch (error) {
                /* ignore error */
            }
        } else {
            // Role is still the same, no need to make API call
            closeEditing();
        }
    }, [selectedMember, appDataDispatch, props.homeId, uiDispatch, closeEditing]);


    /**
     * Handler for when the user click Save input from the member form
     */
    const onMemberInfoFormSubmit = useCallback(async (inputs: BaseHomeMemberInfoUpdatableInputs) => {
        if (!project) {
            return;
        }

        const { email, phoneNumber, role } = inputs;

        // First, check to see if we are using this container to create new user or updating an existing user. If creating new user, call
        // the appropriate function to handle the new user type.
        if (props.userId && role) {
            // if this is for editing existing member
            onUpdateMember(props.userId, role);
        } else if (!props.userId) {
            // if this is for creating new member
            if (props.newMemberSettings && userAccountMode === UserAccountMode.EMAIL) {
                onAddMemberByEmail(email, role);
            } else if (props.newMemberSettings && userAccountMode === UserAccountMode.PHONE) {
                onAddMemberByPhone(phoneNumber, role);
            }
        }
    }, [props.newMemberSettings, props.userId, userAccountMode, onAddMemberByEmail, onAddMemberByPhone, onUpdateMember, project]);


    /**
     * Create Input settings for the MemberInfoComp and cache it so that it doesn't get created on every re-render
     */
    const inputsSettings = useMemo((): HomeMemberInfoProps['fields'] => {

        const defaultSettings: HomeMemberInfoProps['fields'] = {
            role: {
                label       : capitalizeAll(trans('label.role')),
                displayValue: selectedMember ? selectedMember.role.toUpperCase() : '',
                value       : selectedMember?.role,
                options     : [{
                    label: trans(getHomeMemberRoleName(MemberRole.OWNER)).toUpperCase(),
                    value: MemberRole.OWNER,
                }, {
                    label: trans(getHomeMemberRoleName(MemberRole.MEMBER)).toUpperCase(),
                    value: MemberRole.MEMBER,
                }],
                editable: true,
            },
        };

        if (selectedMember) {
            return {
                ...defaultSettings,
                verified: {
                    label       : capitalizeAll(trans('label.status')),
                    displayValue: capitalizeAll(trans(selectedMember.verified ? 'label.verified' : 'label.not_verified')),
                    value       : selectedMember.verified,
                },
                name: {
                    label       : capitalizeAll(trans('label.user_name')),
                    displayValue: selectedMember?.name || '',
                    value       : selectedMember?.name,
                },
                userId: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: selectedMember.userId.toString(),
                    value       : selectedMember.userId,
                },
                email: {
                    label       : capitalizeAll(trans('label.user_email')),
                    displayValue: selectedMember.email || capitalizeAll(trans('label.none')),
                    value       : selectedMember.email,
                },
                phoneNumber: {
                    label       : capitalizeAll(trans('label.user_phone_number')),
                    displayValue: selectedMember.phoneNumber || capitalizeAll(trans('label.none')),
                    value       : selectedMember.phoneNumber,
                },
                creationTime: {
                    label       : capitalizeAll(trans('label.date_created')),
                    displayValue: selectedMember ? getDateTimeAndElapsedTime(selectedMember.creationTime, trans) : '',
                    value       : selectedMember?.creationTime,
                },
            };
        }

        if (!selectedMember && userAccountMode === UserAccountMode.EMAIL) {
            return {
                ...defaultSettings,
                email: {
                    label       : capitalizeAll(trans('label.user_email')),
                    displayValue: '',
                    value       : '',
                    editable    : true,
                    error       : inputErrors.email ? trans(inputErrors.email) : '',
                },
            };
        }

        if (!selectedMember && userAccountMode === UserAccountMode.PHONE) {
            return {
                ...defaultSettings,
                phoneNumber: {
                    label       : capitalizeAll(trans('label.user_phone_number')),
                    displayValue: '',
                    value       : '',
                    editable    : true,
                    error       : inputErrors.phoneNumber ? trans(inputErrors.phoneNumber) : '',
                },
            };
        }

        return defaultSettings;
    }, [selectedMember, inputErrors, userAccountMode, trans]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newMemberSettings !== undefined) {
            props.newMemberSettings.onNewMemberCanceled();
        }
    }, [props.newMemberSettings, closeEditing]);



    /**
     * Build a list of Country dial code options to be passed to the PhoneNumberInput field
     */
    const countryDialCodeOption = useMemo(() => {
        return Constants.SUPPORTED_COUNTRIES.map((country: CountryData): CountryDialCodeOption => {
            return {
                name    : country.code2,
                dialCode: country.phone.dialCode,
            };
        });
    }, []);

    const defaultCountryDialCode = useMemo(() => {
        const defaultLocale = project && project.supportToolSettings && project.supportToolSettings.locale ? project.supportToolSettings.locale : '';
        const defaultCountry = Utils.getCountryByLocale(Constants.SUPPORTED_COUNTRIES_JSON, defaultLocale) || Constants.DEFAULT_COUNTRY;
        return {
            name    : defaultCountry.code2,
            dialCode: defaultCountry.phone.dialCode,
        };
    }, [project]);


    return (
        <>
            {project && (selectedMember || !props.userId) && (
                <HomeMemberInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(props.newMemberSettings ? 'label.add_member' : 'label.member_info'))}
                    icon={trans('icons.user')}
                    fields={inputsSettings}
                    countryDialCodeOptions={countryDialCodeOption}
                    defaultCountryOption={defaultCountryDialCode}
                    texts={{
                        country: capitalizeAll(trans('label.country')),
                    }}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : !selectedMember ? capitalizeAll(trans('label.add')) : capitalizeAll(trans('label.save')),
                            onClick: onMemberInfoFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onMemberInfoFormInputChange}
                />
            )}

            {(!project || (props.userId && !selectedMember)) && (
                <CircularProgress />
            )}
        </>
    );
};
